<template>
  <div v-rect-loading="loading" class="edit-email-template">
    <div class="header">
      <div class="title">{{ $t(titleI18nKey) }}</div>
    </div>
    <div class="content">
      <WrappedFrom
        ref="formEditor"
        :model="form"
        :formLines="formLines"
        :disabled="isViewMode"
      >
        <template #tinyMceEditor="{ line }">
          <h5 class="email-title">{{ $t(`${i18nPrefix}.label.emailHeader`) }}</h5>
          <TinymceEditor
            v-if="showTinyMceEditor"
            v-model="form[line.props.headerKey]"
            class="rich-text-header"
            :disabled="isViewMode"
          >
          </TinymceEditor>
          <h5 class="email-title">{{ $t(`${i18nPrefix}.label.emailFooter`) }}</h5>
          <TinymceEditor
            v-if="showTinyMceEditor"
            v-model="form[line.props.footerKey]"
            class="rich-text-footer"
            :disabled="isViewMode"
          >
          </TinymceEditor>
        </template>
      </WrappedFrom>
      <div class="bottom-btn-group">
        <el-button class="cancel-btn" @click="toLayoutList">{{ $t('common.btn.cancel') }}</el-button>
        <el-button class="save-btn" :disabled="isViewMode" @click="saveOrUpdateLayoutTemplate">{{ $t('common.btn.save') }}</el-button>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions } from 'vuex'
import { validateEmail } from '@/utils/validator'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
import TinymceEditor from '../tinymceEditor.vue'

export default {
  components: {
    TinymceEditor,
    WrappedFrom
  },
  data () {
    return {
      loading: false,
      i18nPrefix: 'admin.company.notification',
      showTinyMceEditor: false,
      form: {
        email_sender_address: null,
        sender_name_cn: null,
        sender_name_en: null,
        content_header_cn: '',
        content_footer_cn: '',
        content_header_en: '',
        content_footer_en: '',
        name: null,
        company: null
      }
    }
  },
  computed: {
    titleI18nKey () {
      return this.$route.meta.titleI18nKey
    },
    layoutTemplateId () {
      return this.$route.params.layoutTemplateId
    },
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    isViewMode () {
      return this.$route.meta.isViewMode
    },
    refForm () {
      return this.$refs.formEditor
    },
    formLines () {
      const requiredMessage = this.$t('common.message.requiredFormField')
      return {
        name: {
          name: 'name',
          label: this.$t('admin.company.notification.label.layoutName'),
          disabled: this.isViewMode,
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        },
        email_sender_address: {
          label: this.$t('admin.company.notification.label.emailSenderAddress'),
          disabled: this.isViewMode,
          rules: [
            { required: true, message: requiredMessage, trigger: 'blur' },
            { validator: validateEmail, trigger: ['blur', 'change'] }
          ]
        },
        sender_name_cn: {
          label: this.$t('admin.company.notification.label.senderEmailCn'),
          disabled: this.isViewMode,
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        },
        sender_name_en: {
          label: this.$t('admin.company.notification.label.senderEmailEn'),
          disabled: this.isViewMode
        },
        content_cn: {
          label: this.$t(`${this.i18nPrefix}.label.emailTemplateCn`),
          contentSlot: 'tinyMceEditor',
          props: {
            headerKey: 'content_header_cn',
            footerKey: 'content_footer_cn'
          }
        },
        content_en: {
          label: this.$t(`${this.i18nPrefix}.label.emailTemplateEn`),
          contentSlot: 'tinyMceEditor',
          props: {
            headerKey: 'content_header_en',
            footerKey: 'content_footer_en'
          }
        }
      }
    }
  },
  created () {
    this.loadTemplate()
  },
  methods: {
    ...mapActions('notificationTemplate', ['createSystemLayoutTemplate', 'updateSystemLayoutTemplate', 'getSystemLayoutTemplateById']),
    async saveOrUpdateLayoutTemplate () {
      const params = {
        ...this.form,
        id: this.layoutTemplateId,
        system_template: true
      }
      params.content_header_cn = this.nodeToHtml(this.form.content_header_cn, true)
      params.content_footer_cn = this.nodeToHtml(this.form.content_footer_cn, false)
      params.content_header_en = this.nodeToHtml(this.form.content_header_en, true)
      params.content_footer_en = this.nodeToHtml(this.form.content_footer_en, false)
      let result = false
      result = await this.refForm.submit()
      if (!result) {
        return
      }
      let resp = null
      this.$eventBus.$emit('showLoading', true)
      try {
        if (this.isEditMode) {
          resp = await this.updateSystemLayoutTemplate(params)
        } else {
          resp = await this.createSystemLayoutTemplate(params)
        }
        if (resp) {
          this.toLayoutList()
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async loadTemplate () {
      this.loading = true
      try {
        if (this.layoutTemplateId) {
          const resp = await this.getSystemLayoutTemplateById(this.layoutTemplateId)
          if (resp) {
            this.form = resp
            this.form.content_header_cn = this.removeNonEditableClass(this.form.content_header_cn, true)
            this.form.content_header_en = this.removeNonEditableClass(this.form.content_header_en, true)
            this.form.content_footer_cn = this.removeNonEditableClass(this.form.content_footer_cn, false)
            this.form.content_footer_en = this.removeNonEditableClass(this.form.content_footer_en, false)
          }
        }
        this.showTinyMceEditor = true
      } finally {
        this.loading = false
      }
    },
    nodeToHtml (oldValue, checkHeader) {
      if (oldValue == null) {
        oldValue = ''
      }
      let result = '<div class="mceNonEditable">' + oldValue + '</div>'
      if (checkHeader) {
        result = '<email-header>' + result + '</email-header>'
      } else {
        result = '<email-footer>' + result + '</email-footer>'
      }
      return result
    },
    removeNonEditableClass (value, checkHeader) {
      if (value !== null) {
        value = value.replace('class="mceNonEditable"', '')
        if (checkHeader) {
          value = value.replace('<email-header>', '')
          value = value.replace('</email-header>', '')
        } else {
          value = value.replace('<email-footer>', '')
          value = value.replace('</email-footer>', '')
        }
      }
      return value
    },
    toLayoutList () {
      this.$router.push({
        path: '/v-admin/system-notification-template-management/layout/list',
        query: this.$route.query
      })
    }
  },
  watch: {
    '$i18n.locale': {
      handler () {
        this.showTinyMceEditor = false
        this.$nextTick(() => {
          this.showTinyMceEditor = true
        })
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.edit-email-template {
  .header {
    display: flex;
    border-bottom: 1px solid #e4e7ed;

    .title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }

  .content {
    width: 980px;
    margin: 30px auto 0;

    .email-title {
      margin-top: 22px;
      position: absolute;
      left: -75px;
      color: #606266;
    }

    .rich-text-footer {
      margin-top: 22px;
    }

    .bottom-btn-group {
      display: flex;
      justify-content: flex-end;

      .el-button {
        padding: 8px 28px;
        border-radius: 2px;
        line-height: 22px;
        font-size: 16px;
        font-weight: 500;
        color: #0096cc;
      }

      .save-btn {
        margin-left: 24px;
        margin-right: 0;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }
    }
  }
}

.page-en {
  .edit-email-template {
    .content {
      .email-title {
        left: -102px;
      }
    }
  }
}
</style>
